import { PageController } from '@client/components/_core/layout/PageController';
import { type TabItem, Tabs } from '@client/components/_core/navigation/Tabs';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { useQueryErrorEffect } from '@client/hooks/api/useQueryErrorEffect';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { ICONS } from '@client/styles/theme';
import type { ShareableRouteContext } from '@client/types/router';
import { createFileRoute } from '@tanstack/react-router';
import { Skeleton } from 'antd';
import { match } from 'ts-pattern';
import { serviceProviderFormStore } from './store';

export const Route = createFileRoute('/console/$orgId/mspr/service-providers/details/$id')({
    component: ServiceProviderDetails,
    params: {
        parse: ({ id }) => ({ id: BigInt(id) }),
    },
    staticData: {
        presentation: 'fullPage',
        ui: {
            widthVariant: 'default',
        },
        entity: 'msprProvider',
    },
    beforeLoad(ctx): ShareableRouteContext {
        async function title() {
            const provider = await ctx.context.trpcUtils.mspr.msp.getProvider.ensureData({ id: ctx.params.id });
            return <span>Service provider detail • {provider.businessName}</span>;
        }

        async function breadcrumb() {
            const provider = await ctx.context.trpcUtils.mspr.msp.getProvider.ensureData({ id: ctx.params.id });
            return provider.businessName;
        }

        return {
            ui: {
                title,
            },
            breadcrumb: {
                title: breadcrumb,
            },
        };
    },
});

function ServiceProviderDetails() {
    const { orgId } = Route.useParams();
    const navigate = useNavigate();

    const { id } = Route.useParams();
    const { trpc, trpcUtils } = useTrpc();

    const { mutateAsync: updateServiceProviderMutation } = trpc.mspr.msp.updateProvider.useMutation();

    const tabItems: TabItem[] = [
        {
            to: '/console/$orgId/mspr/service-providers/details/$id/manage' as const,
            label: (
                <span className="flex items-center gap-2">
                    <ICONS.manage size={16} />
                    Manage MSP
                </span>
            ),
        },
        {
            to: '/console/$orgId/mspr/service-providers/details/$id/library' as const,
            label: (
                <span className="flex items-center gap-2">
                    <ICONS.library size={16} />
                    Library
                </span>
            ),
        },
        {
            to: '/console/$orgId/mspr/service-providers/details/$id/history' as const,
            label: (
                <span className="flex items-center gap-2">
                    <ICONS.history size={16} />
                    History
                </span>
            ),
        },
    ];

    // Fetch service provider data with tRPC query
    const providerQuery = trpc.mspr.msp.getProvider.useQuery(
        { id },
        {
            enabled: !!id,
        },
    );
    useQueryErrorEffect(providerQuery.error, (error) => {
        message.destroy();

        match(error.data?.errorId)
            .with('recordNotFound', () => {
                void errorMessage
                    .showAsync(
                        "The service provider you are trying to access either doesn't exist or you don't have permissions to access it.",
                    )
                    .then(() => {
                        navigate({ to: '/console/$orgId/mspr/service-providers', params: { orgId } });
                    });
            })
            .otherwise(() => {
                errorMessage.show(error);
            });
    });

    const { isLoading } = serviceProviderFormStore.useInit({
        entity: 'msprProvider',
        data: providerQuery.data,
        isLoading: providerQuery.isPending,

        async onReload() {
            await providerQuery.refetch();
        },

        async onSave(currentValue, newValue) {
            try {
                // Convert null ownerId to undefined to match schema expectations
                const sanitizedValue = {
                    ...newValue,
                    ownerId: newValue.ownerId === null ? undefined : newValue.ownerId,
                };

                await updateServiceProviderMutation({ id: currentValue.id, set: sanitizedValue });

                await trpcUtils.mspr.msp.invalidate();

                return { success: true };
            } catch (e) {
                return { success: false, error: e };
            }
        },
    });

    if (isLoading) {
        return <Skeleton active />;
    }

    return (
        <PageController route={Route}>
            <Tabs items={tabItems} />
        </PageController>
    );
}
